import { useState, useEffect } from 'react';
import {
  Star,
  Send,
  Plus,
  Trash2,
  Save,
  Eye,
  BarChart3,
  Mail,
  MessageSquare,
  Smartphone,
  Monitor,
  Check,
  X,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  Smile,
  RefreshCw,
  RotateCcw,
  GripVertical,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Card, Spinner, Badge, useToast } from '@centraoneplus/ui';
import csatService, {
  CsatConfig,
  SurveyBranding,
  SurveyQuestion,
  SurveyTrigger,
  SurveyScale,
  SurveyChannel,
  CsatStats,
} from '@services/csat.service';
import { isSamePayload } from '@utils/formDirty';
import { useUnsavedChangesStore } from '@stores/unsavedChangesStore';
import { ThemedSelect } from '@components/common/ThemedSelect';

/** `#rgb` or `#rrggbb`. Mirrors HEX_COLOR_PATTERN in both CSAT DTOs. */
export const HEX_COLOR_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const DEFAULT_PRIMARY_COLOR = '#3B82F6';

/**
 * Page background used by the preview when the tenant hasn't set one.
 *
 * Deliberately the same value the customer page falls back to
 * (customer-portal FeedbackPage.tsx). The preview used to default to #FFFFFF
 * while the customer saw #F9FAFB, so clearing the colour made the preview show
 * something the customer would never get.
 */
export const SURVEY_PAGE_FALLBACK_BG = '#F9FAFB';

/**
 * Coerce a branding colour to a 6-digit hex, falling back when it can't be.
 *
 * Both colours reach inline styles, and the customer page additionally appends
 * two hex digits of alpha to the primary colour for its tints — so `red`
 * becomes `red50` and `#F00` becomes `#F0050`, both invalid, and the browser
 * drops the declaration silently. Shorthand is expanded because it's a real
 * colour; everything else falls back. Needed on read as well as on write,
 * because nothing validated this column until now.
 */
export function toHex6(value: string | undefined, fallback: string): string {
  const raw = value?.trim() ?? '';
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return fallback;
}

/**
 * What a colour text field should hold once the user leaves it.
 *
 * Normalising on blur rather than on change, because a hex is typed one
 * character at a time and every intermediate state ("#", "#3", "#3B") is
 * invalid — clamping per keystroke the way the numeric inputs do would fight
 * the user. Empty is preserved so "use the default" stays expressible; a bare
 * "3B82F6" gains its "#"; shorthand is expanded; anything still unusable falls
 * back rather than being saved to render nothing.
 */
export function normalizeColorOnBlur(raw: string, fallback: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return toHex6(withHash, fallback).toUpperCase();
}

/**
 * Bounds for the three Timing & Sampling numbers. Mirrored by the BFF and
 * ticket-service DTOs, so a value that passes here passes every layer.
 *
 * `samplePercentage` starts at 1, not 0: 0 is accepted arithmetic but means
 * "survey nobody", and `enabled` is already the switch for that.
 */
export const TIMING_LIMITS = {
  delayHours: { min: 0, max: 168 },
  samplePercentage: { min: 1, max: 100 },
  repeatCooldownDays: { min: 1, max: 365 },
} as const;

/**
 * Sanitize one of the Timing & Sampling number inputs.
 *
 * `min`/`max` on `<input type="number">` constrain the spinner and native form
 * validation — neither of which applies here, since the page has no `<form>`
 * and saves from a button. So a typed 500 or -5 went straight into the payload
 * and came back as an opaque 400 that blocked saving the whole config, naming
 * no field. Emptying the box was worse: `parseInt('')` is NaN, which React
 * warns about, serializes to `null` on the wire, and is then swallowed by the
 * backend's `dto.x ?? base.x` merge — so Save reported success having changed
 * nothing, and the field silently snapped back on re-baseline.
 *
 * Clamping every keystroke means an out-of-range value can't be expressed at
 * all (typing "500" into delay lands on 168) and an emptied box falls back to
 * `min` instead of NaN. Exported for direct testing.
 */
export function clampNumericSetting(raw: string, min: number, max: number): number {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(Math.max(parsed, min), max);
}

/**
 * Whether the working copy of the CSAT config differs from the one last
 * loaded/saved — i.e. whether Save should be enabled and the unsaved-changes
 * warning shown.
 *
 * Uses a value comparison rather than reference or raw `JSON.stringify` because
 * the edit handlers rebuild nested objects (`{ ...config.followUp, ... }`),
 * which can reorder keys while being logically identical. Exported so the
 * round-trip cases can be tested without mounting the whole page.
 */
export function isCsatConfigDirty(
  config: CsatConfig | null,
  savedConfig: CsatConfig | null,
): boolean {
  if (!config || !savedConfig) return false;
  return !isSamePayload(config, savedConfig);
}

/**
 * Why this config can't be saved, or null when it's fine.
 *
 * Surveys are switched on but no trigger is selected is the one combination that
 * looks configured and silently sends nothing — the orchestrator requires the
 * event to match a configured trigger, so an empty list means every ticket is
 * skipped. Same for having no delivery channel: the dispatcher iterates
 * `channels`, so an empty list means nothing is ever delivered.
 *
 * Both are only errors while `enabled` is true; a disabled draft can be saved
 * half-finished. Exported for direct testing.
 */
export function getCsatConfigError(config: CsatConfig | null): string | null {
  if (!config) return null;
  if (!config.enabled) return getNumericRangeError(config);
  if (config.triggers.length === 0) {
    return 'Select at least one survey trigger, or turn Survey Active off. With no trigger, no survey is ever sent.';
  }
  if (config.channels.length === 0) {
    return 'Select at least one delivery channel, or turn Survey Active off. With no channel, surveys are created but never delivered.';
  }
  if (config.questions.length === 0) {
    return 'Add at least one question, or turn Survey Active off. A survey with no questions renders a "please contact support" message to the customer.';
  }
  // A blank question is not a half-finished draft the customer never sees — it
  // ships. They get rating buttons with an empty line above them and no idea
  // what they are rating. Named by position so a long list is easy to fix.
  const blankIndex = config.questions.findIndex((q) => !q.text || q.text.trim() === '');
  if (blankIndex !== -1) {
    return `Question ${blankIndex + 1} has no text. Customers would see rating buttons with nothing to rate.`;
  }
  // 0% is the one in-range-looking number that silently surveys nobody: the
  // orchestrator's gate is `Math.random() * 100 < 0`, which never passes, so
  // every ticket is skipped with a log line no admin sees. Called out here,
  // ahead of the range check, because "must be between 1 and 100" doesn't
  // explain why an apparently-active survey stopped sending.
  if (config.samplePercentage === 0) {
    return 'Sample Percentage is 0%, so no ticket is ever surveyed. Set it between 1 and 100, or turn Survey Active off to pause surveys.';
  }
  const emailError = getEmailTemplateError(config);
  if (emailError) return emailError;
  return getNumericRangeError(config);
}

/**
 * The placeholders the dispatcher substitutes. Anything else ships literally.
 * Must stay identical to SurveyDispatcherService.TEMPLATE_VARIABLES — a test
 * pins the list on both sides.
 *
 * `logo` is optional in a template: when it's absent the branding logo is
 * prepended above the body automatically, so it only needs writing out to
 * control where it sits.
 */
export const TEMPLATE_VARIABLES = [
  'customerName',
  'ticketNumber',
  'surveyWidget',
  'logo',
] as const;

const PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/**
 * Problems with the email subject/template that the customer would otherwise
 * receive, or null when they're fine.
 *
 * Only meaningful once email is an enabled delivery channel — the fields are
 * hidden otherwise, and the dispatcher's built-in fallback template is known
 * good, so an empty template is fine.
 */
export function getEmailTemplateError(config: CsatConfig): string | null {
  if (!config.channels.includes(SurveyChannel.EMAIL)) return null;

  const template = (config.emailTemplate ?? '').trim();

  // An unrecognised placeholder is substituted by nothing, so the customer
  // receives the literal `{{surveyUrl}}` in their email. Both are plausible
  // guesses, and nothing told the admin only three names exist.
  for (const field of [
    { value: config.emailSubject ?? '', label: 'Email Subject' },
    { value: template, label: 'Email Template' },
  ]) {
    for (const [, name] of field.value.matchAll(PLACEHOLDER_PATTERN)) {
      if (!(TEMPLATE_VARIABLES as readonly string[]).includes(name)) {
        return `${field.label} uses {{${name}}}, which isn't a real variable — the customer would see it as-is. Available: ${TEMPLATE_VARIABLES.map((v) => `{{${v}}}`).join(', ')}.`;
      }
    }
  }

  // Empty means "use the built-in template", which does contain the widget. A
  // non-empty template without it sends an email with no way to respond — the
  // same silent dead end as an empty trigger or channel list.
  if (template && !/\{\{\s*surveyWidget\s*\}\}/.test(template)) {
    return 'Email Template has no {{surveyWidget}}, so the customer would get an email with no way to rate anything. Add it, or clear the template to use the default.';
  }

  return null;
}

/**
 * Range violation among the Timing & Sampling numbers, or null when they're all
 * legal.
 *
 * Checked for a **disabled** draft too, unlike the completeness rules above. An
 * empty trigger list is a half-finished draft the backend accepts; an
 * out-of-range number is not — both DTOs reject it whatever `enabled` says. So
 * skipping this check for a disabled config would just trade a clear message
 * for the opaque 400 that names no field, which is the failure this whole
 * function exists to pre-empt. Reachable in practice: `samplePercentage` was
 * `@Min(0)` until this change, so tenants can have a stored 0.
 *
 * The inputs clamp on every keystroke, so anything landing here came from a
 * config saved before that clamp existed, or written through the API.
 */
function getNumericRangeError(config: CsatConfig): string | null {
  const rangeChecks: Array<[keyof typeof TIMING_LIMITS, string]> = [
    ['delayHours', 'Delay After Resolution (hours)'],
    ['samplePercentage', 'Sample Percentage'],
    ['repeatCooldownDays', 'Repeat Cooldown (days)'],
  ];
  for (const [key, label] of rangeChecks) {
    const value = config[key];
    const { min, max } = TIMING_LIMITS[key];
    if (!Number.isInteger(value) || value < min || value > max) {
      return `${label} must be a whole number between ${min} and ${max}.`;
    }
  }
  return getBrandingError(config) ?? getEmailLengthError(config);
}

/**
 * Length violations on the email fields.
 *
 * Unconditional, like the numeric ranges and branding: the DTOs cap these
 * whatever `enabled` and the channel list say, and `email_subject` is
 * VarChar(255) in the database. The BFF had no cap at all, so an over-long
 * subject passed it and 400'd at ticket-service as "Failed to save CSAT
 * settings" with no field named.
 */
function getEmailLengthError(config: CsatConfig): string | null {
  if ((config.emailSubject ?? '').length > 255) {
    return 'Email Subject is too long (max 255 characters).';
  }
  if ((config.emailTemplate ?? '').length > 20000) {
    return 'Email Template is too long (max 20,000 characters).';
  }
  return null;
}

/**
 * Branding problem that would either be rejected by the DTOs or silently fail
 * to render, or null when the branding is fine.
 *
 * Checked whatever `enabled` says, for the same reason as the numeric ranges:
 * the DTOs reject these outright, so letting one through trades a clear message
 * for an opaque 400. All three are reachable today — nothing validated these
 * fields before, so a tenant can already have `red` in a colour column or a
 * bare hostname in the logo URL.
 *
 * Empty is always allowed and means "use the default".
 */
export function getBrandingError(config: CsatConfig): string | null {
  const branding = config.branding ?? {};

  const colours: Array<[keyof SurveyBranding, string]> = [
    ['primaryColor', 'Primary Color'],
    ['backgroundColor', 'Background Color'],
  ];
  for (const [key, label] of colours) {
    const value = (branding[key] ?? '').trim();
    if (value && !HEX_COLOR_PATTERN.test(value)) {
      return `${label} must be a hex colour like ${DEFAULT_PRIMARY_COLOR} — "${value}" won't render.`;
    }
  }

  const logoUrl = (branding.logoUrl ?? '').trim();
  if (logoUrl) {
    // Requiring the scheme is the point: a bare "cdn.example.com/logo.png" is
    // read as a relative path by the portal and renders nothing, and an http://
    // logo is blocked as mixed content on the https portal.
    if (!/^https?:\/\/\S+$/i.test(logoUrl)) {
      return 'Logo URL must be a full web address starting with https:// (http:// logos are blocked on secure pages).';
    }
    if (logoUrl.length > 2048) {
      return 'Logo URL is too long (max 2048 characters).';
    }
  }

  if ((branding.thankYouMessage ?? '').length > 1000) {
    return 'Thank You Message is too long (max 1000 characters).';
  }

  return null;
}

/**
 * Drag wrapper for one question row. Mirrors SortableStageItem in
 * pipeline-config/StagesTab.tsx — same dnd-kit setup, so the two reorderable
 * lists in Settings behave identically.
 */
function SortableQuestionItem({
  id,
  children,
}: {
  id: string;
  // dnd-kit's attribute/listener maps have no index signature, so they're typed
  // loosely for the spread onto the grip handle.
  children: (handle: {
    attributes: any;
    listeners: any;
    isDragging: boolean;
  }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners, isDragging })}
    </div>
  );
}

/**
 * Renders one rating widget. Pulled out of SurveyPreview so the same widget can
 * be reused per question — the preview used to render a single hardcoded
 * `config.questions[0]`, so a multi-question survey could not be previewed at
 * all (issue 6).
 */
function PreviewScaleWidget({
  scale,
  selected,
  onSelect,
}: {
  scale: SurveyScale;
  selected: number | null;
  onSelect: (rating: number) => void;
}) {
  const selectedRating = selected;
  const setSelectedRating = onSelect;

  // No `dark:` variants anywhere in here. This widget renders inside the
  // preview's white card, which mirrors the customer page — and the customer
  // page has no dark mode at all, because it wears the tenant's branding rather
  // than the agent app's theme. Carrying app dark-mode classes onto that light
  // surface is what made the preview unreadable in dark mode.
  const renderScale = () => {
    switch (scale) {
      // Both star scales render here. stars_10 used to fall through to the NPS
      // branch below, so "10-Star Rating" previewed as a numbered 1-10 row —
      // matching the same bug on the customer-facing feedback page.
      case SurveyScale.STARS_5:
      case SurveyScale.STARS_10: {
        const starCount = scale === SurveyScale.STARS_10 ? 10 : 5;
        return (
          <div className="flex gap-1 flex-wrap justify-center">
            {Array.from({ length: starCount }, (_, i) => i + 1).map((rating) => (
              <button
                key={rating}
                onClick={() => setSelectedRating(rating)}
                className="transition-transform hover:scale-110"
                aria-label={`${rating} of ${starCount} stars`}
              >
                <Star
                  className={`${starCount === 10 ? 'h-6 w-6' : 'h-8 w-8'} ${
                    selectedRating && selectedRating >= rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        );
      }
      case SurveyScale.THUMBS:
        return (
          <div className="flex gap-6">
            <button
              onClick={() => setSelectedRating(1)}
              className={`p-4 rounded-full transition-all ${
                selectedRating === 1 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
            >
              <ThumbsDown className="h-8 w-8" />
            </button>
            <button
              onClick={() => setSelectedRating(5)}
              className={`p-4 rounded-full transition-all ${
                selectedRating === 5 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
            >
              <ThumbsUp className="h-8 w-8" />
            </button>
          </div>
        );
      case SurveyScale.EMOJI:
        const emojis = ['😞', '😕', '😐', '🙂', '😊'];
        return (
          <div className="flex gap-2">
            {emojis.map((emoji, index) => (
              <button
                key={index}
                onClick={() => setSelectedRating(index + 1)}
                className={`text-3xl p-2 rounded-lg transition-all ${
                  selectedRating === index + 1 ? 'bg-primary-100 scale-110' : 'hover:bg-gray-100'
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        );
      // Renders 1-10, but real NPS is 0-10 (as the scale dropdown claims).
      // Correcting it reaches the submit validators, the 1..10 rating
      // distribution, and the nps calculation — tracked separately.
      case SurveyScale.NPS:
        return (
          <div className="flex gap-1">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((rating) => (
              <button
                key={rating}
                onClick={() => setSelectedRating(rating)}
                className={`w-8 h-8 rounded text-sm font-medium transition-all ${
                  selectedRating === rating
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {rating}
              </button>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return <div className="flex justify-center my-4">{renderScale()}</div>;
}

/**
 * Preview of the survey as the customer receives it.
 *
 * Renders EVERY configured question, each with its own scale and labels —
 * previously it rendered only `questions[0]`, so a multi-question survey could
 * not be checked before going live (issue 6). Mirrors the customer-facing
 * FeedbackPage: numbered when there is more than one, and the follow-up prompt
 * keys off the FIRST question's rating, since that is the primary score there.
 *
 * `scaleOverride` collapses it back to a single widget of one given scale — that
 * is what the "Scale Previews" card uses to show each scale side by side.
 */
function SurveyPreview({
  config,
  scaleOverride,
}: {
  config: Partial<CsatConfig>;
  scaleOverride?: SurveyScale;
}) {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [logoFailed, setLogoFailed] = useState(false);

  // The customer page normalises this too, since nothing validated the column
  // before now — an unusable value has to fall back rather than silently
  // producing no colour at all.
  const previewPrimaryColor = toHex6(config.branding?.primaryColor, DEFAULT_PRIMARY_COLOR);

  const questions = config.questions ?? [];
  const fallbackQuestion = {
    id: '__preview__',
    text: 'How satisfied are you with our service?',
    scale: scaleOverride ?? SurveyScale.STARS_5,
    required: true,
    lowLabel: undefined,
    highLabel: undefined,
  };

  // With an override we show one widget of that scale (using the first
  // question's wording when there is one); otherwise the whole survey.
  const shown = scaleOverride
    ? [{ ...(questions[0] ?? fallbackQuestion), scale: scaleOverride, id: '__preview__' }]
    : questions.length > 0
      ? questions
      : [fallbackQuestion];

  const primaryRating = shown.length > 0 ? answers[shown[0].id] : undefined;
  const followUpThreshold = config.followUp?.showBelowRating || 3;
  const showFollowUp =
    !!config.followUp?.enabled &&
    primaryRating !== undefined &&
    primaryRating <= followUpThreshold;

  return (
    // Two layers, matching the customer page: the branded colour is the PAGE
    // background, and the survey sits on a white card. It used to be painted
    // straight onto the card, which misrepresented every non-white colour an
    // admin could pick — they'd see a coloured card, the customer gets a
    // coloured page around a white one.
    <div
      className="p-6"
      style={{ backgroundColor: config.branding?.backgroundColor?.trim() || SURVEY_PAGE_FALLBACK_BG }}
    >
      <div className="bg-white rounded-xl shadow-sm p-6 text-center">
        {config.branding?.logoUrl && !logoFailed && (
          <img
            src={config.branding.logoUrl}
            alt="Logo"
            className="h-10 mx-auto mb-4"
            onError={() => setLogoFailed(true)}
          />
        )}
        {config.branding?.logoUrl && logoFailed && (
          <p className="text-xs text-amber-600 mb-4">
            Logo didn't load — check the URL is reachable and served over https.
          </p>
        )}

        <div className="space-y-6">
          {shown.map((question, index) => (
            <div key={question.id}>
              {shown.length > 1 && (
                <p className="text-xs text-gray-400 mb-1">
                  Question {index + 1} of {shown.length}
                </p>
              )}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {question.text || 'Untitled question'}
                {(question.required || index === 0) && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </h3>
              {question.lowLabel && question.highLabel && (
                <div className="flex justify-between text-xs text-gray-500 mb-2 px-4">
                  <span>{question.lowLabel}</span>
                  <span>{question.highLabel}</span>
                </div>
              )}
              <PreviewScaleWidget
                scale={question.scale}
                selected={answers[question.id] ?? null}
                onSelect={(rating) => setAnswers((prev) => ({ ...prev, [question.id]: rating }))}
              />
            </div>
          ))}
        </div>

        {showFollowUp && (
          <div className="mt-4">
            <textarea
              placeholder={config.followUp?.questionText || 'What could we improve?'}
              className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none"
              rows={3}
            />
          </div>
        )}
        <Button className="mt-4" style={{ backgroundColor: previewPrimaryColor }}>
          Submit Feedback
        </Button>
      </div>
    </div>
  );
}

/**
 * The post-submit confirmation screen, mirroring the customer page's
 * ResultBanner — branded background, logo, tick, thank-you message.
 *
 * The fallback text matches the customer page's own default, so an admin who
 * leaves Thank You Message blank sees the words the customer will actually get
 * rather than an empty box.
 */
function ThankYouPreview({ config }: { config: Partial<CsatConfig> }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const branding = config.branding ?? {};

  return (
    <div
      className="p-6"
      style={{ backgroundColor: branding.backgroundColor?.trim() || SURVEY_PAGE_FALLBACK_BG }}
    >
      <div className="bg-white rounded-xl shadow-sm p-8 flex flex-col items-center text-center">
        {branding.logoUrl && !logoFailed && (
          <img
            src={branding.logoUrl}
            alt="Logo"
            className="h-10 mb-6 object-contain"
            onError={() => setLogoFailed(true)}
          />
        )}
        <Check className="h-10 w-10 text-green-500 mb-3" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Thank you!</h2>
        <p className="text-sm text-gray-600 max-w-md">
          {branding.thankYouMessage?.trim() ||
            'Your feedback has been recorded. We appreciate your time.'}
        </p>
      </div>
    </div>
  );
}

// Stats Dashboard Component
function StatsPanel({ stats }: { stats: CsatStats | null }) {
  if (!stats) return null;

  const satisfaction = csatService.getSatisfactionLevel(stats.averageScore);

  return (
    // pt-4 gives the score tiles breathing room below the sticky header now that
    // the surrounding layout padding is only 1rem.
    <div className="grid grid-cols-4 gap-4 mb-6 pt-4">
      <Card className="p-4 text-center">
        <div className={`text-3xl font-bold ${satisfaction.color}`}>
          {stats.averageScore.toFixed(1)}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Average Score</div>
        <Badge className="mt-2" variant={stats.averageScore >= 4 ? 'success' : 'warning'}>
          {satisfaction.label}
        </Badge>
      </Card>
      <Card className="p-4 text-center">
        <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalResponses}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total Responses</div>
      </Card>
      <Card className="p-4 text-center">
        <div className="text-3xl font-bold text-primary-600">{stats.responseRate.toFixed(1)}%</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Response Rate</div>
      </Card>
      <Card className="p-4">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Distribution</div>
        <div className="space-y-1">
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = stats.distribution[rating] || 0;
            const percentage = stats.totalResponses > 0 ? (count / stats.totalResponses) * 100 : 0;
            return (
              <div key={rating} className="flex items-center gap-2 text-xs">
                <span className="w-4">{rating}★</span>
                <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${rating >= 4 ? 'bg-green-500' : rating >= 3 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="w-8 text-right text-gray-500 dark:text-gray-400">{count}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

export default function CsatSettings() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<CsatConfig | null>(null);
  // The config as last loaded/saved. Every edit below mutates `config` only, so
  // comparing the two is what tells us whether there is anything to save.
  const [savedConfig, setSavedConfig] = useState<CsatConfig | null>(null);
  const [stats, setStats] = useState<CsatStats | null>(null);
  const [activeTab, setActiveTab] = useState<'config' | 'preview' | 'stats'>('config');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const hasChanges = isCsatConfigDirty(config, savedConfig);
  const configError = getCsatConfigError(config);

  useEffect(() => {
    loadData();
  }, []);

  // Publish dirty state so the settings tab links (in the parent layout) can
  // warn before navigating away, and clear it on unmount.
  const setUnsavedChanges = useUnsavedChangesStore((state) => state.setIsDirty);
  useEffect(() => {
    setUnsavedChanges(hasChanges);
    return () => setUnsavedChanges(false);
  }, [hasChanges, setUnsavedChanges]);

  // Warn on browser refresh / tab close while there are unsaved edits.
  useEffect(() => {
    if (!hasChanges) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configData, statsData] = await Promise.all([
        csatService.getConfig(),
        csatService.getStats(),
      ]);
      setConfig(configData);
      setSavedConfig(configData);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to load CSAT configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const updated = await csatService.updateConfig(config);
      // Re-baseline from the server's response, not the payload we sent — the
      // backend replaces question rows and hands back fresh ids, so echoing our
      // own copy would leave the form permanently "dirty".
      setConfig(updated);
      setSavedConfig(updated);
      toast.success('CSAT settings saved successfully');
    } catch (error) {
      toast.error('Failed to save CSAT settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (savedConfig) setConfig(savedConfig);
  };

  const handleToggleEnabled = () => {
    if (!config) return;
    setConfig({ ...config, enabled: !config.enabled });
  };

  const handleTriggerToggle = (trigger: SurveyTrigger) => {
    if (!config) return;
    const triggers = config.triggers.includes(trigger)
      ? config.triggers.filter((t) => t !== trigger)
      : [...config.triggers, trigger];
    setConfig({ ...config, triggers });
  };

  const handleChannelToggle = (channel: SurveyChannel) => {
    if (!config) return;
    const channels = config.channels.includes(channel)
      ? config.channels.filter((c) => c !== channel)
      : [...config.channels, channel];
    setConfig({ ...config, channels });
  };

  const handleQuestionUpdate = (index: number, updates: Partial<SurveyQuestion>) => {
    if (!config) return;
    const questions = [...config.questions];
    questions[index] = { ...questions[index], ...updates };
    setConfig({ ...config, questions });
  };

  const handleAddQuestion = () => {
    if (!config) return;
    const newQuestion: SurveyQuestion = {
      id: `q${Date.now()}`,
      text: 'New question',
      scale: SurveyScale.STARS_5,
      required: true,
    };
    setConfig({ ...config, questions: [...config.questions, newQuestion] });
  };

  const handleRemoveQuestion = (index: number) => {
    if (!config || config.questions.length <= 1) return;
    const questions = config.questions.filter((_, i) => i !== index);
    setConfig({ ...config, questions });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /**
   * Reorder questions by drag. Unlike StagesTab this does NOT persist
   * immediately — question order is part of the unsaved form, and the backend
   * derives `sortOrder` from array position when `syncQuestions` rewrites the
   * rows on Save. So a reorder simply makes the form dirty, and Reset undoes it.
   */
  const handleQuestionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!config || !over || active.id === over.id) return;

    const oldIndex = config.questions.findIndex((q) => q.id === active.id);
    const newIndex = config.questions.findIndex((q) => q.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    setConfig({ ...config, questions: arrayMove(config.questions, oldIndex, newIndex) });
  };

  const handleSendTestSurvey = async () => {
    if (!config) return;
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setSendingTest(true);
    try {
      // Send the working copy, not just the address — otherwise the backend
      // renders from the last saved row and the admin tests the previous
      // template without being told.
      const { id: _id, tenantId: _tenantId, ...working } = config;
      const result = await csatService.sendTestSurvey(testEmail, working);
      if (result.success) {
        toast.success(result.message);
        setTestEmail('');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to send test survey');
    } finally {
      setSendingTest(false);
    }
  };

  const getChannelIcon = (channel: SurveyChannel) => {
    switch (channel) {
      case SurveyChannel.EMAIL:
        return Mail;
      case SurveyChannel.CHAT:
        return MessageSquare;
      case SurveyChannel.SMS:
        return Smartphone;
      case SurveyChannel.WIDGET:
        return Monitor;
      default:
        return Mail;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Failed to load CSAT configuration</p>
        <Button variant="outline" onClick={loadData} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    // `-mt-4` cancels the settings layout's p-4 top padding so the sticky header
    // below sits flush against the settings header. It lives here, on a static
    // parent, rather than on the sticky element itself: a sticky box resolves its
    // offset inside its containing block, so a negative margin on the sticky
    // element gets clamped and the gap survives.
    // NOTE: the 4s here must track the layout's padding in SettingsPage's
    // TenantSettingsLayout content area — they cancel it exactly.
    <div className="space-y-6 -mt-4">
      {/* Header — sticky so the Survey Active toggle and Save stay reachable while
          scrolling the long configuration form. `-mx-4 px-4` bleeds the bar across
          the layout's side gutters so cards can't show through beside it as they
          scroll underneath; `py-4` supplies its internal spacing. */}
      <div className="sticky top-0 z-20 -mx-4 px-4 py-4 flex items-center justify-between gap-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Satisfaction Surveys</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Configure and manage CSAT surveys for your support tickets</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {configError ? (
            <span className="text-xs text-red-600 dark:text-red-400 max-w-xs text-right">
              {configError}
            </span>
          ) : (
            hasChanges && (
              <span className="text-xs text-amber-600 dark:text-amber-400">You have unsaved changes</span>
            )
          )}
          <div className="flex items-center gap-2">
            <span id="csat-active-label" className="text-sm text-gray-600 dark:text-gray-400">
              Survey Active
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={config.enabled}
              aria-labelledby="csat-active-label"
              onClick={handleToggleEnabled}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${
                config.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                  config.enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <Button variant="outline" onClick={handleReset} disabled={!hasChanges || saving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges || !!configError}
            title={configError ?? (!hasChanges ? 'No changes to save' : undefined)}
          >
            {saving ? <Spinner size="sm" className="mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Stats Panel */}
      <StatsPanel stats={stats} />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          {[
            { id: 'config', label: 'Configuration', icon: Star },
            { id: 'preview', label: 'Preview', icon: Eye },
            { id: 'stats', label: 'Analytics', icon: BarChart3 },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div className="space-y-6">
          {/* Triggers */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Survey Triggers</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Choose when to send CSAT surveys to customers</p>
            <div className="grid grid-cols-2 gap-3">
              {csatService.getTriggerOptions().map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleTriggerToggle(option.value)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    config.triggers.includes(option.value)
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div
                    className={`h-5 w-5 rounded-full flex items-center justify-center ${
                      config.triggers.includes(option.value)
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700'
                    }`}
                  >
                    {config.triggers.includes(option.value) && <Check className="h-3 w-3" />}
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{option.label}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Channels */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Delivery Channels</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Select how surveys will be delivered</p>
            <div className="grid grid-cols-2 gap-3">
              {csatService.getChannelOptions().map((option) => {
                const Icon = getChannelIcon(option.value);
                return (
                  <button
                    key={option.value}
                    onClick={() => handleChannelToggle(option.value)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      config.channels.includes(option.value)
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${config.channels.includes(option.value) ? 'text-primary-600' : 'text-gray-400'} dark:text-gray-500`} />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Questions */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Survey Questions</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Configure the questions in your survey.
                  {config.questions.length > 1 && ' Drag by the handle to reorder — the first question is the one that produces your score.'}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={handleAddQuestion}>
                <Plus className="h-4 w-4 mr-1" />
                Add Question
              </Button>
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleQuestionDragEnd}
            >
              <SortableContext
                items={config.questions.map((q) => q.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4">
                  {config.questions.map((question, index) => (
                    <SortableQuestionItem key={question.id} id={question.id}>
                      {({ attributes, listeners, isDragging }) => (
                        <div className={`p-4 bg-gray-50 dark:bg-gray-800 rounded-lg ${isDragging ? 'ring-2 ring-primary-400' : ''}`}>
                          <div className="flex items-start gap-4">
                            {/* Drag handle. Only offered when there is something to
                                reorder, and it carries the position so the first
                                question's special role is visible. */}
                            {config.questions.length > 1 && (
                              <button
                                type="button"
                                {...attributes}
                                {...listeners}
                                aria-label={`Reorder question ${index + 1}`}
                                className="mt-1 flex flex-col items-center gap-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                              >
                                <GripVertical className="h-4 w-4" />
                                <span className="text-[10px] font-medium tabular-nums">{index + 1}</span>
                              </button>
                            )}
                            <div className="flex-1 space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Question Text
                                </label>
                                <input
                                  type="text"
                                  value={question.text}
                                  onChange={(e) => handleQuestionUpdate(index, { text: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Scale Type
                                  </label>
                                  <ThemedSelect
                                    value={question.scale}
                                    onChange={(e) => handleQuestionUpdate(index, { scale: e.target.value as SurveyScale })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  >
                                    {csatService.getScaleOptions().map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </ThemedSelect>
                                </div>
                                <div className="flex items-center gap-4 pt-5">
                                  <label className="flex items-center gap-2 text-sm">
                                    <input
                                      type="checkbox"
                                      checked={question.required}
                                      onChange={(e) => handleQuestionUpdate(index, { required: e.target.checked })}
                                      className="rounded text-primary-600"
                                    />
                                    Required
                                  </label>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Low Label
                                  </label>
                                  <input
                                    type="text"
                                    value={question.lowLabel || ''}
                                    onChange={(e) => handleQuestionUpdate(index, { lowLabel: e.target.value })}
                                    placeholder="e.g., Very Dissatisfied"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    High Label
                                  </label>
                                  <input
                                    type="text"
                                    value={question.highLabel || ''}
                                    onChange={(e) => handleQuestionUpdate(index, { highLabel: e.target.value })}
                                    placeholder="e.g., Very Satisfied"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  />
                                </div>
                              </div>
                            </div>
                            {config.questions.length > 1 && (
                              <button
                                onClick={() => handleRemoveQuestion(index)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors dark:text-gray-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </SortableQuestionItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </Card>

          {/* Follow-up Settings */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Follow-up Question</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Optionally ask for more feedback on low ratings</p>
              </div>
              <button
                onClick={() =>
                  setConfig({
                    ...config,
                    followUp: { ...config.followUp, enabled: !config.followUp.enabled },
                  })
                }
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  config.followUp.enabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    config.followUp.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {config.followUp.enabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Follow-up Question Text
                  </label>
                  <input
                    type="text"
                    value={config.followUp.questionText || ''}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        followUp: { ...config.followUp, questionText: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="What could we have done better?"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Show follow-up for ratings below
                  </label>
                  <ThemedSelect
                    value={config.followUp.showBelowRating || 3}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        followUp: { ...config.followUp, showBelowRating: parseInt(e.target.value, 10) },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {[2, 3, 4].map((n) => (
                      <option key={n} value={n}>
                        {n} stars or below
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
              </div>
            )}
          </Card>

          {/* Timing & Sampling */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Timing & Sampling</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Delay After Resolution (hours)
                </label>
                <input
                  type="number"
                  min={TIMING_LIMITS.delayHours.min}
                  max={TIMING_LIMITS.delayHours.max}
                  value={config.delayHours}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      delayHours: clampNumericSetting(
                        e.target.value,
                        TIMING_LIMITS.delayHours.min,
                        TIMING_LIMITS.delayHours.max,
                      ),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Wait before sending survey (0–168, max 7 days). The response window starts when
                  the survey is sent, so a long delay no longer shortens it.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sample Percentage
                </label>
                <input
                  type="number"
                  min={TIMING_LIMITS.samplePercentage.min}
                  max={TIMING_LIMITS.samplePercentage.max}
                  value={config.samplePercentage}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      samplePercentage: clampNumericSetting(
                        e.target.value,
                        TIMING_LIMITS.samplePercentage.min,
                        TIMING_LIMITS.samplePercentage.max,
                      ),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  % of tickets to survey (1–100). To stop surveys entirely, turn Survey Active off.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="excludeRepeats"
                  checked={config.excludeRepeats}
                  onChange={(e) => setConfig({ ...config, excludeRepeats: e.target.checked })}
                  className="rounded text-primary-600"
                />
                <label htmlFor="excludeRepeats" className="text-sm text-gray-700 dark:text-gray-300">
                  Exclude repeat customers
                </label>
              </div>
              {/* Only has any effect while "Exclude repeat customers" is on — the
                  orchestrator never reads it otherwise, so it's disabled rather
                  than left looking live. */}
              <div>
                <label
                  htmlFor="repeatCooldownDays"
                  className={`block text-xs font-medium mb-1 ${
                    config.excludeRepeats
                      ? 'text-gray-700 dark:text-gray-300'
                      : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  Repeat Cooldown (days)
                </label>
                <input
                  id="repeatCooldownDays"
                  type="number"
                  min={TIMING_LIMITS.repeatCooldownDays.min}
                  max={TIMING_LIMITS.repeatCooldownDays.max}
                  value={config.repeatCooldownDays}
                  disabled={!config.excludeRepeats}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      repeatCooldownDays: clampNumericSetting(
                        e.target.value,
                        TIMING_LIMITS.repeatCooldownDays.min,
                        TIMING_LIMITS.repeatCooldownDays.max,
                      ),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {config.excludeRepeats
                    ? 'How long a customer is left alone after being surveyed (1–365).'
                    : 'Enable "Exclude repeat customers" to apply a cooldown.'}
                </p>
              </div>
            </div>
          </Card>

          {/* Branding */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Survey Branding</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Primary Color
                </label>
                <div className="flex items-center gap-2">
                  {/* toHex6, not `|| default`: <input type="color"> silently
                      coerces anything that isn't #rrggbb to black, so a stored
                      "red" showed a black swatch. */}
                  <input
                    type="color"
                    value={toHex6(config.branding.primaryColor, DEFAULT_PRIMARY_COLOR)}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        branding: { ...config.branding, primaryColor: e.target.value },
                      })
                    }
                    className="h-10 w-14 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  {/* Free text so a hex can be typed a character at a time,
                      normalised on blur — #f00 becomes #FF0000, and anything
                      unusable reverts rather than being saved to silently
                      render nothing. Without this the colour swatch beside it
                      coerces an invalid value to black while this field still
                      shows what was typed, so the two disagree. */}
                  <input
                    type="text"
                    value={config.branding.primaryColor ?? DEFAULT_PRIMARY_COLOR}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        branding: { ...config.branding, primaryColor: e.target.value },
                      })
                    }
                    onBlur={(e) =>
                      setConfig({
                        ...config,
                        branding: {
                          ...config.branding,
                          primaryColor: normalizeColorOnBlur(e.target.value, DEFAULT_PRIMARY_COLOR),
                        },
                      })
                    }
                    placeholder={DEFAULT_PRIMARY_COLOR}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Background Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={toHex6(config.branding.backgroundColor, '#FFFFFF')}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        branding: { ...config.branding, backgroundColor: e.target.value },
                      })
                    }
                    className="h-10 w-14 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={config.branding.backgroundColor ?? '#FFFFFF'}
                    onChange={(e) =>
                      setConfig({
                        ...config,
                        branding: { ...config.branding, backgroundColor: e.target.value },
                      })
                    }
                    onBlur={(e) =>
                      setConfig({
                        ...config,
                        branding: {
                          ...config.branding,
                          backgroundColor: normalizeColorOnBlur(e.target.value, '#FFFFFF'),
                        },
                      })
                    }
                    placeholder="#FFFFFF"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={config.branding.logoUrl || ''}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      branding: { ...config.branding, logoUrl: e.target.value },
                    })
                  }
                  placeholder="https://example.com/logo.png"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Must be a full https:// address reachable from outside your network. Check the
                  Preview tab to confirm it loads.
                </p>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Thank You Message
                </label>
                <textarea
                  value={config.branding.thankYouMessage || ''}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      branding: { ...config.branding, thankYouMessage: e.target.value },
                    })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Thank you for your feedback!"
                />
              </div>
            </div>
          </Card>

          {/* Email Settings */}
          {config.channels.includes(SurveyChannel.EMAIL) && (
            <Card className="p-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Email Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={config.emailSubject || ''}
                    onChange={(e) => setConfig({ ...config, emailSubject: e.target.value })}
                    // Matches the DTOs and the VarChar(255) column, so an
                    // over-long subject can't be typed into an opaque 400.
                    maxLength={255}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="How was your support experience?"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Template (HTML)
                  </label>
                  <textarea
                    value={config.emailTemplate || ''}
                    onChange={(e) => setConfig({ ...config, emailTemplate: e.target.value })}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Use {{customerName}}, {{ticketNumber}}, {{surveyWidget}} as placeholders"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Available variables: {'{{customerName}}'}, {'{{ticketNumber}}'},{' '}
                    {'{{surveyWidget}}'}, {'{{logo}}'}. Only these are substituted — anything else
                    reaches the customer as-is. Leave the template empty to use the built-in one.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Your Branding logo is added above the message automatically. Use {'{{logo}}'} only
                    if you want to place it somewhere else.
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="Enter email to send test"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Button
                    variant="outline"
                    onClick={handleSendTestSurvey}
                    disabled={sendingTest || !!configError}
                    title={configError ?? undefined}
                  >
                    {sendingTest ? <Spinner size="sm" className="mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Send Test
                  </Button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Uses what's on screen, including unsaved edits. The emailed link stays valid for
                  24 hours and can be submitted — test responses are excluded from your analytics.
                </p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Survey Preview</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Every question, in order, exactly as the customer receives it.
            </p>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* No scaleOverride — render the whole configured survey. */}
              <SurveyPreview config={config} />
            </div>
          </Card>

          {/* The confirmation screen. Previously unpreviewable anywhere, even
              though Thank You Message sits in the Branding card and is the only
              field whose entire purpose is to be read after submitting. */}
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              After Submitting
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              What the customer sees once they've sent their rating — also shown if they open the
              link a second time.
            </p>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <ThankYouPreview config={config} />
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Scale Previews</h3>
            <div className="grid grid-cols-2 gap-4">
              {csatService.getScaleOptions().map((option) => (
                <div key={option.value} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{option.label}</span>
                  </div>
                  <SurveyPreview config={config} scaleOverride={option.value} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Satisfaction Trend (Last 7 Days)</h3>
            <div className="h-48 flex items-end gap-2">
              {stats.trend.map((day, index) => {
                const maxHeight = 160;
                const height = (day.average / 5) * maxHeight;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-xs text-gray-500 dark:text-gray-400">{day.average.toFixed(1)}</div>
                    <div
                      className={`w-full rounded-t ${
                        day.average >= 4 ? 'bg-green-500' : day.average >= 3 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ height: `${height}px` }}
                    />
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Response Distribution</h3>
            <div className="space-y-3">
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = stats.distribution[rating] || 0;
                const percentage = stats.totalResponses > 0 ? (count / stats.totalResponses) * 100 : 0;
                return (
                  <div key={rating} className="flex items-center gap-3">
                    <div className="w-20 flex items-center gap-1">
                      {Array.from({ length: rating }).map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          rating >= 4 ? 'bg-green-500' : rating >= 3 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="w-16 text-right text-sm text-gray-600 dark:text-gray-400">{count} ({percentage.toFixed(0)}%)</div>
                  </div>
                );
              })}
            </div>
          </Card>

          <ChannelBreakdownCard stats={stats} />
        </div>
      )}
    </div>
  );
}

// Stacked horizontal bar chart of CSAT responses grouped by delivery channel.
// Each row is one channel (email, chat, widget, sms). The bar segments show
// what proportion of that channel's responses fell into low (1-2 red),
// medium (3 yellow), and high (4-5 green) rating buckets — so admins can
// see at a glance whether one channel skews more positive than another.
function ChannelBreakdownCard({ stats }: { stats: CsatStats }) {
  const channels = stats.channelBreakdown ? Object.entries(stats.channelBreakdown) : [];
  const totalCount = channels.reduce((acc, [, c]) => acc + c.count, 0);

  if (channels.length === 0 || totalCount === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">By Channel</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          No channel data yet. Once customers respond, their feedback grouped by channel
          (email, chat, widget, SMS) will appear here.
        </p>
      </Card>
    );
  }

  // Find the largest channel count so all bars share a comparable scale.
  const maxCount = Math.max(...channels.map(([, c]) => c.count), 1);

  const channelLabel: Record<string, string> = {
    email: 'Email',
    chat: 'Live Chat',
    widget: 'Web Widget',
    sms: 'SMS',
    unknown: 'Other',
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">By Channel</h3>
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-green-500" /> 4–5 (positive)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-yellow-500" /> 3 (neutral)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm bg-red-500" /> 1–2 (negative)
          </span>
        </div>
      </div>
      <div className="space-y-4">
        {channels
          .sort(([, a], [, b]) => b.count - a.count)
          .map(([channel, c]) => {
            const high = (c.ratingDistribution?.[4] || 0) + (c.ratingDistribution?.[5] || 0);
            const mid = c.ratingDistribution?.[3] || 0;
            const low = (c.ratingDistribution?.[1] || 0) + (c.ratingDistribution?.[2] || 0);
            const total = high + mid + low;
            const widthPct = (c.count / maxCount) * 100;
            return (
              <div key={channel} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {channelLabel[channel] || channel}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {c.count} response{c.count === 1 ? '' : 's'} · avg {c.avgRating.toFixed(1)}
                  </span>
                </div>
                <div className="relative h-7 bg-gray-100 dark:bg-gray-800 rounded-md overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 flex"
                    style={{ width: `${widthPct}%` }}
                    title={`${c.count} responses on ${channelLabel[channel] || channel}`}
                  >
                    {total > 0 && high > 0 && (
                      <div
                        className="bg-green-500 flex items-center justify-center text-[10px] font-semibold text-white"
                        style={{ width: `${(high / total) * 100}%` }}
                        title={`${high} positive (4–5)`}
                      >
                        {high}
                      </div>
                    )}
                    {total > 0 && mid > 0 && (
                      <div
                        className="bg-yellow-500 flex items-center justify-center text-[10px] font-semibold text-white"
                        style={{ width: `${(mid / total) * 100}%` }}
                        title={`${mid} neutral (3)`}
                      >
                        {mid}
                      </div>
                    )}
                    {total > 0 && low > 0 && (
                      <div
                        className="bg-red-500 flex items-center justify-center text-[10px] font-semibold text-white"
                        style={{ width: `${(low / total) * 100}%` }}
                        title={`${low} negative (1–2)`}
                      >
                        {low}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </Card>
  );
}
